export interface AllocatableEntry {
  id: string
  direction: 'OWED_TO_US' | 'WE_OWE'
  outstandingPaise: number
  entryDate: string
}

function sideTotal(entries: AllocatableEntry[], dir: AllocatableEntry['direction']): number {
  return entries.filter((e) => e.direction === dir).reduce((s, e) => s + e.outstandingPaise, 0)
}

export function fullNet(entries: AllocatableEntry[]): {
  netPaise: number
  direction: 'IN' | 'OUT' | 'EVEN'
} {
  const net = sideTotal(entries, 'OWED_TO_US') - sideTotal(entries, 'WE_OWE')
  return { netPaise: Math.abs(net), direction: net > 0 ? 'IN' : net < 0 ? 'OUT' : 'EVEN' }
}

/** Settles the smaller side of the ticked entries in full (it's what offsets the payment), then
 *  spreads `paid + smaller side` over the larger side, oldest first (spec D3). Mirrors the
 *  server's validation, so a valid result here is a valid POST /settlements body. */
export function allocate(
  entries: AllocatableEntry[],
  paidPaise: number
): { entryId: string; amountPaise: number }[] {
  const { netPaise, direction } = fullNet(entries)
  if (paidPaise < 0 || paidPaise > netPaise)
    throw new Error('Amount must be between ₹0 and the net owed')
  const major: AllocatableEntry['direction'] = direction === 'OUT' ? 'WE_OWE' : 'OWED_TO_US'
  const minor: AllocatableEntry['direction'] = major === 'OWED_TO_US' ? 'WE_OWE' : 'OWED_TO_US'
  const minorEntries = entries.filter((e) => e.direction === minor)
  const out = minorEntries.map((e) => ({ entryId: e.id, amountPaise: e.outstandingPaise }))
  let budget = paidPaise + sideTotal(entries, minor)
  const majorOldestFirst = entries
    .filter((e) => e.direction === major)
    .sort((a, b) => a.entryDate.localeCompare(b.entryDate))
  const majorOut: { entryId: string; amountPaise: number }[] = []
  for (const e of majorOldestFirst) {
    if (budget <= 0) break
    const take = Math.min(budget, e.outstandingPaise)
    majorOut.push({ entryId: e.id, amountPaise: take })
    budget -= take
  }
  return [...out, ...majorOut].filter((a) => a.amountPaise > 0)
}

/** Cancels what's owed each way against each other with no money moving: the smaller side in
 *  full, the same amount off the larger side, both oldest first. */
export function cancelOut(entries: AllocatableEntry[]): {
  cancelledPaise: number
  allocations: { entryId: string; amountPaise: number }[]
} {
  const cancelledPaise = Math.min(sideTotal(entries, 'OWED_TO_US'), sideTotal(entries, 'WE_OWE'))
  const allocations: { entryId: string; amountPaise: number }[] = []
  for (const dir of ['OWED_TO_US', 'WE_OWE'] as const) {
    let budget = cancelledPaise
    for (const e of entries
      .filter((x) => x.direction === dir)
      .sort((a, b) => a.entryDate.localeCompare(b.entryDate))) {
      if (budget <= 0) break
      const take = Math.min(budget, e.outstandingPaise)
      allocations.push({ entryId: e.id, amountPaise: take })
      budget -= take
    }
  }
  return { cancelledPaise, allocations }
}
